import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { Kunde, ProtokolSektion, ProtokolFeldOptionen } from '../types';
import kundeService from '../services/kundeService';
import protokollService from '../services/protokollService';
import protokollSchemaService from '../services/protokollSchemaService';
import { Icon, LoadingScreen, fehlerText } from '../components/ui';
import { ZurueckLink } from '../components/ZurueckLink';
import { markiereNeu } from '../utils/ankunft';
import { DatumFeld } from '../components/DatumZeit';

const today = () => new Date().toISOString().split('T')[0];

/** Lokaler Sicherungsstand je Protokoll, damit beim Neuladen oder Akkuende nichts verloren geht. */
const entwurfSchluessel = (id?: string) => `protokoll_entwurf_${id ?? 'neu'}`;

/** Wartezeit nach der letzten Eingabe, bevor ein Entwurf automatisch auf dem Server landet */
const AUTO_SPEICHERN_MS = 1500;

interface Formularstand {
  kundeId: number | '';
  kundeSearch: string;
  datum: string;
  teilnehmer: string;
  gewaehrleistungBeginn: string;
  maengel: string;
  antworten: Record<number, string>;
  unterschriftName: string;
  signaturePreview: string | null;
}

const LEERER_STAND: Omit<Formularstand, 'datum'> = {
  kundeId: '', kundeSearch: '', teilnehmer: '', gewaehrleistungBeginn: '', maengel: '',
  antworten: {}, unterschriftName: '', signaturePreview: null,
};

// Leere Antworten zählen nicht als Änderung (z. B. „Auswahl aufheben“)
const serialisiere = (stand: Formularstand) => JSON.stringify({
  ...stand,
  antworten: Object.fromEntries(Object.entries(stand.antworten).filter(([, v]) => v !== '')),
});

const parseOptionen = (json: string | null): ProtokolFeldOptionen => {
  if (!json) return {};
  try { return JSON.parse(json); } catch { return {}; }
};

/** Schneidet die Signatur auf den beschriebenen Bereich zu */
const cropSignatureCanvas = (canvas: HTMLCanvasElement): string | null => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  let minX = width, minY = height, maxX = 0, maxY = 0;
  let hasContent = false;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      if (data[idx] < 200 || data[idx + 1] < 200 || data[idx + 2] < 200) {
        hasContent = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!hasContent) return null;
  const pad = 20;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;
  const tmp = document.createElement('canvas');
  tmp.width = cropW;
  tmp.height = cropH;
  const tmpCtx = tmp.getContext('2d')!;
  tmpCtx.fillStyle = '#fff';
  tmpCtx.fillRect(0, 0, cropW, cropH);
  tmpCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
  return tmp.toDataURL('image/png');
};

const InbetriebnahmeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  // Id des gespeicherten Protokolls. Bei neuen Protokollen setzt sie das erste automatische Speichern –
  // ohne Routenwechsel, denn der würde das Formular neu aufbauen und nach oben springen.
  const [gespeicherteId, setGespeicherteId] = useState<number | null>(id ? Number(id) : null);
  const vorhanden = gespeicherteId !== null;
  const schluesselId = vorhanden ? String(gespeicherteId) : undefined;

  const [sektionen, setSektionen] = useState<ProtokolSektion[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [zuletztGespeichert, setZuletztGespeichert] = useState<Date | null>(null);
  const [autoFehler, setAutoFehler] = useState<string | null>(null);
  const [istEntwurf, setIstEntwurf] = useState(false);

  // Kopffelder
  const [kundeId, setKundeId] = useState<number | ''>('');
  const [datum, setDatum] = useState(today());
  const [teilnehmer, setTeilnehmer] = useState('');
  const [gewaehrleistungBeginn, setGewaehrleistungBeginn] = useState('');
  const [maengel, setMaengel] = useState('');

  // Dynamische Antworten
  const [antworten, setAntworten] = useState<Record<number, string>>({});

  // Kundensuche
  const [kundeSearch, setKundeSearch] = useState('');
  const [kundeDropdownOpen, setKundeDropdownOpen] = useState(false);
  const kundeDropdownRef = useRef<HTMLDivElement>(null);

  // Signatur
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [existingSignature, setExistingSignature] = useState<string | null>(null);
  const [unterschriftName, setUnterschriftName] = useState('');

  // Mobil-Modal
  const modalCanvasRef = useRef<HTMLCanvasElement>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [isModalDrawing, setIsModalDrawing] = useState(false);

  // Lokale Sicherung: `basisRef` ist der zuletzt geladene/gespeicherte Stand, Abweichungen landen im localStorage
  const basisRef = useRef<string | null>(null);
  const lokalerEntwurfRef = useRef<{ savedAt: string; stand: Formularstand } | null>(null);
  const [entwurfBanner, setEntwurfBanner] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (kundeDropdownRef.current && !kundeDropdownRef.current.contains(e.target as Node)) {
        setKundeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const load = async () => {
      let basis: Formularstand = { ...LEERER_STAND, datum: today() };
      try {
        const [schema, kundenData] = await Promise.all([
          protokollSchemaService.getSchema(),
          kundeService.getAll(),
        ]);
        setSektionen(schema.filter(s => s.aktiv));
        setKunden(kundenData);

        if (isEdit && id) {
          const p = await protokollService.getById(Number(id));
          setKundeId(p.kundeId);
          setDatum(p.datum);
          setTeilnehmer(p.teilnehmer || '');
          setGewaehrleistungBeginn(p.gewaehrleistungBeginn || '');
          setMaengel(p.maengel || '');
          setUnterschriftName(p.unterschriftName || '');
          setIstEntwurf(p.entwurf);
          if (p.unterschriftBild) setExistingSignature(p.unterschriftBild);
          const k = kundenData.find(x => x.id === p.kundeId);
          const kundeName = k ? [k.vorname, k.nachnameFirma].filter(Boolean).join(' ') : '';
          if (k) setKundeSearch(kundeName);
          const loaded: Record<number, string> = {};
          for (const [feldId, wert] of Object.entries(p.antworten)) {
            if (wert) loaded[Number(feldId)] = wert;
          }
          setAntworten(loaded);
          basis = {
            kundeId: p.kundeId, kundeSearch: kundeName, datum: p.datum, teilnehmer: p.teilnehmer || '',
            gewaehrleistungBeginn: p.gewaehrleistungBeginn || '', maengel: p.maengel || '',
            antworten: loaded, unterschriftName: p.unterschriftName || '', signaturePreview: null,
          };
        }
      } catch {
        toast.error(isEdit ? 'Protokoll konnte nicht geladen werden' : 'Formular konnte nicht geladen werden');
      } finally {
        basisRef.current = serialisiere(basis);
        try {
          const gesichert = localStorage.getItem(entwurfSchluessel(id));
          if (gesichert) {
            lokalerEntwurfRef.current = JSON.parse(gesichert);
            setEntwurfBanner(true);
          }
        } catch { /* kaputte Sicherung ignorieren */ }
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const stand = serialisiere({
    kundeId, kundeSearch, datum, teilnehmer, gewaehrleistungBeginn, maengel, antworten, unterschriftName,
    signaturePreview: hasSigned ? signaturePreview : null,
  });
  const geaendert = basisRef.current !== null && stand !== basisRef.current;

  // Jede Eingabe sofort lokal sichern (nicht solange die Wiederherstellen-Frage offen ist)
  useEffect(() => {
    if (loading || entwurfBanner || basisRef.current === null) return;
    try {
      if (geaendert) {
        localStorage.setItem(entwurfSchluessel(schluesselId), JSON.stringify({ savedAt: new Date().toISOString(), stand: JSON.parse(stand) }));
      } else {
        localStorage.removeItem(entwurfSchluessel(schluesselId));
      }
    } catch { /* Speicher voll o. Ä.: dann eben ohne Sicherung */ }
  }, [stand, geaendert, loading, entwurfBanner, schluesselId]);

  const entwurfWiederherstellen = () => {
    const e = lokalerEntwurfRef.current?.stand;
    if (e) {
      setKundeId(e.kundeId);
      setKundeSearch(e.kundeSearch);
      setDatum(e.datum);
      setTeilnehmer(e.teilnehmer);
      setGewaehrleistungBeginn(e.gewaehrleistungBeginn);
      setMaengel(e.maengel);
      setAntworten(e.antworten);
      setUnterschriftName(e.unterschriftName);
      if (e.signaturePreview) {
        setSignaturePreview(e.signaturePreview);
        setHasSigned(true);
        setExistingSignature(null);
      }
    }
    lokalerEntwurfRef.current = null;
    setEntwurfBanner(false);
  };

  const entwurfVerwerfen = () => {
    localStorage.removeItem(entwurfSchluessel(schluesselId));
    lokalerEntwurfRef.current = null;
    setEntwurfBanner(false);
  };

  const abbrechen = () => {
    if (geaendert && !window.confirm('Nicht gespeicherte Eingaben verwerfen?')) return;
    localStorage.removeItem(entwurfSchluessel(schluesselId));
    navigate('/protokolle');
  };

  // Neue Protokolle und Entwürfe speichern sich selbst; abgeschlossene nur per „Speichern“
  const autoSpeichern = !vorhanden || istEntwurf;

  /** Entwurf schließen: Letzte Eingaben noch speichern, dann zur Liste. */
  const schliessen = async () => {
    if (geaendert && kundeId && datum) {
      if (!(await speichern(true, true))) return;
    }
    navigate('/protokolle');
  };

  useEffect(() => {
    if (!autoSpeichern || !geaendert || !kundeId || !datum || loading || entwurfBanner || saving) return;
    const t = setTimeout(() => { speichern(true, true); }, AUTO_SPEICHERN_MS);
    return () => clearTimeout(t);
    // `speichern` entsteht je Rendern neu; maßgeblich ist der Formularstand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand, autoSpeichern, geaendert, kundeId, datum, loading, entwurfBanner, saving]);

  const setAntwort = (feldId: number, wert: string) => {
    setAntworten(prev => ({ ...prev, [feldId]: wert }));
  };

  const filteredKunden = kunden.filter(k => {
    const name = [k.vorname, k.nachnameFirma].filter(Boolean).join(' ').toLowerCase();
    return name.includes(kundeSearch.toLowerCase());
  });
  const selectedKunde = kunden.find(k => k.id === kundeId);

  // ---- Signatur: Desktop ----

  const getCanvasCoords = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: ((e as React.MouseEvent).clientX - rect.left) * scaleX,
      y: ((e as React.MouseEvent).clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const { x, y } = getCanvasCoords(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSigned(true);
    setExistingSignature(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const { x, y } = getCanvasCoords(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing]);

  const stopDrawing = useCallback(() => setIsDrawing(false), []);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
    setExistingSignature(null);
    setSignaturePreview(null);
  };

  // ---- Signatur: Mobil-Modal ----

  const openSignatureModal = () => {
    setSignatureModalOpen(true);
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      const canvas = modalCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const w = window.innerWidth;
      const h = window.innerHeight - 60;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(30, h * 0.7);
      ctx.lineTo(w - 30, h * 0.7);
      ctx.stroke();
    }, 50);
  };

  const closeSignatureModal = (save: boolean) => {
    if (save && modalCanvasRef.current) {
      const cropped = cropSignatureCanvas(modalCanvasRef.current);
      if (cropped) {
        setSignaturePreview(cropped);
        setHasSigned(true);
        setExistingSignature(null);
      } else {
        toast.error('Bitte zuerst unterschreiben');
        return;
      }
    }
    setSignatureModalOpen(false);
    setIsModalDrawing(false);
    document.body.style.overflow = '';
  };

  const clearModalCanvas = () => {
    const canvas = modalCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, h * 0.7);
    ctx.lineTo(w - 30, h * 0.7);
    ctx.stroke();
  };

  const getModalCoords = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left),
        y: (e.touches[0].clientY - rect.top),
      };
    }
    return {
      x: ((e as React.MouseEvent).clientX - rect.left) / dpr,
      y: ((e as React.MouseEvent).clientY - rect.top) / dpr,
    };
  };

  const startModalDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = modalCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const { x, y } = getModalCoords(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsModalDrawing(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isModalDrawing) return;
    const canvas = modalCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const { x, y } = getModalCoords(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalDrawing]);

  const stopModalDrawing = useCallback(() => setIsModalDrawing(false), []);

  // ---- Submit ----

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    speichern(false);
  };

  /**
   * `alsEntwurf`: als Entwurf speichern und weiter bearbeiten (automatisch, `still` ohne Toast),
   * sonst abschließen und zur Liste. Liefert, ob gespeichert wurde.
   */
  const speichern = async (alsEntwurf: boolean, still = false): Promise<boolean> => {
    if (!kundeId) { toast.error('Bitte Kunden auswählen'); return false; }
    if (!datum) { toast.error('Bitte Datum angeben'); return false; }
    if (savingRef.current) return false;

    // Unterschrift ermitteln
    let unterschriftBild: string | undefined;
    if (signaturePreview && hasSigned) {
      unterschriftBild = signaturePreview;
    } else if (hasSigned && canvasRef.current) {
      unterschriftBild = canvasRef.current.toDataURL('image/png');
    } else if (existingSignature) {
      unterschriftBild = existingSignature;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const req = {
        kundeId: Number(kundeId),
        datum,
        teilnehmer: teilnehmer || undefined,
        gewaehrleistungBeginn: gewaehrleistungBeginn || undefined,
        maengel: maengel || undefined,
        unterschriftBild,
        unterschriftName: unterschriftName || undefined,
        antworten: Object.fromEntries(
          Object.entries(antworten).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        ) as Record<number, string | null>,
        entwurf: alsEntwurf,
      };

      const gespeichert = vorhanden
        ? await protokollService.update(gespeicherteId, req)
        : await protokollService.create(req);
      localStorage.removeItem(entwurfSchluessel(schluesselId));
      basisRef.current = stand;

      setAutoFehler(null);

      if (alsEntwurf) {
        setIstEntwurf(true);
        setZuletztGespeichert(new Date());
        if (!still) toast.success('Protokoll zwischengespeichert');
        if (!vorhanden) {
          // Nur die Adresszeile anpassen (Neuladen öffnet dann den Entwurf), Router-Zustand bleibt erhalten
          setGespeicherteId(gespeichert.id);
          window.history.replaceState(window.history.state, '', `/protokolle/${gespeichert.id}/bearbeiten`);
        }
        return true;
      }

      markiereNeu(`protokoll-${gespeichert.id}`);
      toast.success(!vorhanden ? 'Protokoll angelegt' : istEntwurf ? 'Protokoll abgeschlossen' : 'Protokoll gespeichert');
      navigate('/protokolle');
      return true;
    } catch (err: any) {
      // Automatisches Speichern meldet sich leise am Formularende statt mit einem Toast je Versuch
      if (still) setAutoFehler(fehlerText(err, 'Entwurf konnte nicht gespeichert werden'));
      else toast.error(fehlerText(err, 'Protokoll konnte nicht gespeichert werden'));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const uhrzeit = (d: Date) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div>
          <ZurueckLink fallback="/protokolle" fallbackLabel="Zurück zu den Protokollen" />
          <h1 className="mt-3 text-xl md:text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
            {isEdit ? 'Protokoll bearbeiten' : 'Neues Inbetriebnahmeprotokoll'}
            {istEntwurf && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Entwurf</span>}
          </h1>
        </div>

        {/* Lokal gesicherte Eingaben wiederherstellen */}
        {entwurfBanner && lokalerEntwurfRef.current && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Icon.Warnung className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-amber-800">Nicht gespeicherte Eingaben gefunden</span>
              <span className="text-xs text-amber-600 block">
                {new Date(lokalerEntwurfRef.current.savedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <button type="button" onClick={entwurfWiederherstellen}
              className="flex-shrink-0 text-sm font-medium text-amber-700 hover:text-amber-900 px-2 py-1 rounded hover:bg-amber-100 transition-colors">
              Wiederherstellen
            </button>
            <button type="button" onClick={entwurfVerwerfen}
              className="flex-shrink-0 text-sm text-amber-500 hover:text-amber-700 px-2 py-1 rounded hover:bg-amber-100 transition-colors">
              Verwerfen
            </button>
          </div>
        )}

        {/* Stammfelder */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-800 text-base">Allgemeine Angaben</h2>

          {/* Kunde */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kunde *</label>
            <div className="relative" ref={kundeDropdownRef}>
              <input
                type="text"
                value={kundeSearch}
                onChange={e => { setKundeSearch(e.target.value); setKundeDropdownOpen(true); setKundeId(''); }}
                onFocus={() => setKundeDropdownOpen(true)}
                placeholder="Kunde suchen..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              {kundeDropdownOpen && filteredKunden.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredKunden.slice(0, 20).map(k => (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => {
                        setKundeId(k.id);
                        setKundeSearch([k.vorname, k.nachnameFirma].filter(Boolean).join(' '));
                        setKundeDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 text-gray-700"
                    >
                      <div className="font-medium">{[k.vorname, k.nachnameFirma].filter(Boolean).join(' ')}</div>
                      {(k.strasse || k.ort) && (
                        <div className="text-xs text-gray-400">{[k.strasse, k.plz, k.ort].filter(Boolean).join(', ')}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedKunde && (
              <div className="mt-1 text-xs text-gray-500">
                {[selectedKunde.strasse, selectedKunde.plz, selectedKunde.ort].filter(Boolean).join(', ')}
              </div>
            )}
          </div>

          {/* Datum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Datum *</label>
            <DatumFeld
              value={datum}
              onChange={(v) => setDatum(v)}
              required
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Teilnehmer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teilnehmer</label>
            <input
              type="text"
              value={teilnehmer}
              onChange={e => setTeilnehmer(e.target.value)}
              placeholder="z. B. Moritz Heinze; Ben Gilgenberg"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        {/* Dynamische Sektionen (K wird als fester Block unten gerendert) */}
        {sektionen.filter(s => s.buchstabe !== 'K').map(sektion => (
          <div key={sektion.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3 bg-brand-700 text-white">
              <span className="w-8 h-8 rounded-full bg-white text-brand-700 font-bold text-sm flex items-center justify-center flex-shrink-0">
                {sektion.buchstabe}
              </span>
              <span className="font-semibold">{sektion.titel}</span>
            </div>

            <div className="p-5 space-y-4">
              {sektion.felder.filter(f => f.aktiv).map(feld => {
                const opts = parseOptionen(feld.optionenJson);
                const wert = antworten[feld.id] || '';

                return (
                  <div key={feld.id}>
                    {/* radio_gruppe */}
                    {feld.feldtyp === 'radio_gruppe' && opts.optionen && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{feld.bezeichnung}</label>
                        <div className="flex flex-wrap gap-4">
                          {opts.optionen.map(opt => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`feld_${feld.id}`}
                                value={opt}
                                checked={wert === opt}
                                onChange={() => setAntwort(feld.id, opt)}
                                className="accent-brand-600"
                              />
                              <span className="text-sm text-gray-700">{opt}</span>
                            </label>
                          ))}
                          {wert && (
                            <button type="button" onClick={() => setAntwort(feld.id, '')} className="text-xs text-gray-400 hover:text-gray-600 underline">
                              Auswahl aufheben
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ja_nein */}
                    {feld.feldtyp === 'ja_nein' && (
                      <div className="flex items-center justify-between py-0.5">
                        <span className="text-sm text-gray-700 flex-1 pr-4">{feld.bezeichnung}</span>
                        <div className="flex gap-4 flex-shrink-0">
                          {['Ja', 'Nein'].map(opt => (
                            <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name={`feld_${feld.id}`} value={opt} checked={wert === opt}
                                onChange={() => setAntwort(feld.id, opt)} className="accent-brand-600" />
                              <span className="text-sm text-gray-600">{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ja_nein_ohne */}
                    {feld.feldtyp === 'ja_nein_ohne' && (
                      <div className="flex items-center justify-between py-0.5">
                        <span className="text-sm text-gray-700 flex-1 pr-4">{feld.bezeichnung}</span>
                        <div className="flex gap-4 flex-shrink-0">
                          {['Ja', 'Nein', 'Ohne'].map(opt => (
                            <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name={`feld_${feld.id}`} value={opt} checked={wert === opt}
                                onChange={() => setAntwort(feld.id, opt)} className="accent-brand-600" />
                              <span className="text-sm text-gray-600">{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* text */}
                    {feld.feldtyp === 'text' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{feld.bezeichnung}</label>
                        <input type="text" value={wert} onChange={e => setAntwort(feld.id, e.target.value)}
                          placeholder={opts.placeholder || ''}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                      </div>
                    )}

                    {/* zahl */}
                    {feld.feldtyp === 'zahl' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{feld.bezeichnung}</label>
                        <div className="flex items-center gap-2">
                          <input type="number" step="any" value={wert} onChange={e => setAntwort(feld.id, e.target.value)}
                            placeholder={opts.placeholder || ''}
                            className="w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                          {opts.einheit && <span className="text-sm text-gray-500">{opts.einheit}</span>}
                        </div>
                      </div>
                    )}

                    {/* freitext_liste */}
                    {feld.feldtyp === 'freitext_liste' && opts.labels && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{feld.bezeichnung}</label>
                        <div className="space-y-2">
                          {opts.labels.map((label, idx) => {
                            let listWerte: string[] = [];
                            try { listWerte = wert ? JSON.parse(wert) : []; } catch { listWerte = []; }
                            while (listWerte.length < opts.labels!.length) listWerte.push('');
                            return (
                              <div key={label} className="flex items-center gap-2">
                                <span className="text-sm text-gray-500 w-5 flex-shrink-0">{label})</span>
                                <input type="text" value={listWerte[idx] || ''}
                                  onChange={e => {
                                    const newArr = [...listWerte];
                                    newArr[idx] = e.target.value;
                                    setAntwort(feld.id, JSON.stringify(newArr));
                                  }}
                                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Abnahmebestätigung – immer letzte Sektion */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3 bg-brand-700 text-white">
            <span className="w-8 h-8 rounded-full bg-white text-brand-700 font-bold text-sm flex items-center justify-center flex-shrink-0">
              K
            </span>
            <span className="font-semibold">Abnahmebestätigung</span>
          </div>
          <div className="p-5 space-y-5">
            <p className="text-xs text-gray-500 leading-relaxed">
              Nachstehend wird durch Unterschrift die fachgerechte Übergabe und Unterweisung,
              insbesondere auch für Überwinterung, Außerbetrieb- und Inbetriebnahme bestätigt.
            </p>

            {/* Dynamische Felder aus Sektion K (z. B. Ort) */}
            {sektionen.find(s => s.buchstabe === 'K')?.felder.filter(f => f.aktiv).map(feld => {
              const opts = parseOptionen(feld.optionenJson);
              const wert = antworten[feld.id] || '';
              return (
                <div key={feld.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{feld.bezeichnung}</label>
                  <input
                    type="text"
                    value={wert}
                    onChange={e => setAntwort(feld.id, e.target.value)}
                    placeholder={opts.placeholder || ''}
                    className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              );
            })}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Festgestellte Mängel</label>
              <textarea value={maengel} onChange={e => setMaengel(e.target.value)} rows={4}
                placeholder="Mängel werden nachstehend aufgeführt und bis zum ________ behoben."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gewährleistung beginnt am</label>
              <DatumFeld value={gewaehrleistungBeginn} onChange={(v) => setGewaehrleistungBeginn(v)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>

            {/* Unterschrift */}
            <div className="border-t border-gray-100 pt-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Unterschrift Auftraggeber</h3>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name des Unterzeichners</label>
                <input type="text" value={unterschriftName} onChange={e => setUnterschriftName(e.target.value)}
                  placeholder="Name eingeben"
                  className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>

              {/* Vorhandene Unterschrift (Bearbeiten-Modus) */}
              {existingSignature && !hasSigned && (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Vorhandene Unterschrift</label>
                  <div className="border-2 border-gray-200 rounded-lg p-2 bg-white inline-block">
                    <img src={existingSignature} alt="Vorhandene Unterschrift" className="max-h-24" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Neu zeichnen, um die Unterschrift zu ersetzen.</p>
                </div>
              )}

              {/* Mobil: Button → Vollbild-Modal */}
              <div className="md:hidden mb-2">
                {signaturePreview && hasSigned ? (
                  <div>
                    <div className="border-2 border-brand-300 rounded-lg p-2 bg-white">
                      <img src={signaturePreview} alt="Unterschrift" className="w-full max-h-32 object-contain" />
                    </div>
                    <div className="flex gap-3 mt-2">
                      <button type="button" onClick={openSignatureModal} className="text-sm text-brand-600 hover:text-brand-800 font-medium">Neu unterschreiben</button>
                      <button type="button" onClick={clearSignature} className="text-sm text-gray-500 hover:text-gray-700">Löschen</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={openSignatureModal}
                    className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 hover:border-brand-400 transition-colors flex flex-col items-center gap-2">
                    <Icon.Unterschrift className="w-8 h-8 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">Tippen zum Unterschreiben</span>
                    <span className="text-xs text-gray-400">Öffnet Vollbild</span>
                  </button>
                )}
              </div>

              {/* Desktop: Inline-Canvas */}
              <div className="hidden md:block">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {existingSignature && !hasSigned ? 'Neue Unterschrift (optional)' : 'Unterschrift'}
                </label>
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white" style={{ touchAction: 'none' }}>
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className="w-full cursor-crosshair"
                    style={{ height: '150px' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <button type="button" onClick={clearSignature} className="mt-2 text-sm text-gray-500 hover:text-gray-700">
                  Unterschrift löschen
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Aktions-Buttons */}
        <div className="flex flex-wrap items-center gap-3 justify-end pb-8">
          {/* Stand des automatischen Speicherns */}
          {autoSpeichern && (
            <p className={`mr-auto text-xs ${autoFehler ? 'text-red-600' : 'text-gray-500'}`} aria-live="polite">
              {autoFehler
                ? autoFehler
                : !kundeId
                  ? 'Wird automatisch gespeichert, sobald ein Kunde gewählt ist'
                  : saving
                    ? 'Wird gespeichert …'
                    : geaendert
                      ? 'Ungespeicherte Änderungen'
                      : zuletztGespeichert
                        ? `Entwurf gespeichert um ${uhrzeit(zuletztGespeichert)}`
                        : istEntwurf ? 'Entwurf, wird automatisch gespeichert' : null}
            </p>
          )}
          {autoSpeichern && vorhanden ? (
            <button type="button" onClick={schliessen} disabled={saving}
              title="Entwurf behalten und später weiter ausfüllen"
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-60">
              Später weiter
            </button>
          ) : (
            <button type="button" onClick={abbrechen}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
              Abbrechen
            </button>
          )}
          <button type="submit" disabled={saving}
            className="px-5 py-2.5 bg-brand-700 text-white rounded-lg hover:bg-brand-800 transition-colors text-sm font-medium disabled:opacity-60">
            {!vorhanden ? 'Protokoll anlegen' : istEntwurf ? 'Protokoll abschließen' : 'Speichern'}
          </button>
        </div>
      </form>

      {/* Vollbild-Signatur-Modal (mobil) */}
      {signatureModalOpen && (
        <div className="fixed inset-0 z-[200] bg-white flex flex-col">
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <button type="button" onClick={() => closeSignatureModal(false)}
              className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg text-sm font-medium">
              Abbrechen
            </button>
            <span className="text-sm text-gray-500 font-medium">Unterschrift</span>
            <div className="flex gap-2">
              <button type="button" onClick={clearModalCanvas}
                className="px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg text-sm">
                Löschen
              </button>
              <button type="button" onClick={() => closeSignatureModal(true)}
                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                Übernehmen
              </button>
            </div>
          </div>
          <div className="flex-1 relative" style={{ touchAction: 'none' }}>
            <canvas
              ref={modalCanvasRef}
              className="absolute inset-0 bg-white"
              onMouseDown={startModalDrawing}
              onMouseMove={modalDraw}
              onMouseUp={stopModalDrawing}
              onMouseLeave={stopModalDrawing}
              onTouchStart={startModalDrawing}
              onTouchMove={modalDraw}
              onTouchEnd={stopModalDrawing}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default InbetriebnahmeForm;
